(() => {
  'use strict';
  const VERSION = '15.7.1';
  const GROUP_PROPERTY = '_ME Fulfillment Group';
  const GROUP_LABELS = {
    calca_dropship: 'DTF Supplies',
    local_dtf: 'Apparel & Print items',
    personalized_items: 'Personalized Items',
    wedding: 'Wedding Stationery',
    business: 'Business Services',
    standard: 'standard items'
  };
  const root = () => window.Shopify?.routes?.root || '/';
  const nativeFetch = window.fetch.bind(window);
  const clean = (value) => String(value ?? '').trim();
  const canonicalGroup = (value) => {
    const group = clean(value).toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    if (['local_dtf','apparel_print','blank_apparel','gang_sheet','ready_to_wear','personalized_apparel','custom_apparel','personalized','custom','custom_order'].includes(group)) return 'local_dtf';
    if (['personalized_items','personalized_gifts','engraved_gifts'].includes(group)) return 'personalized_items';
    if (['calca','calca_dropship','dtf_supplies'].includes(group)) return 'calca_dropship';
    if (['wedding','wedding_stationery'].includes(group)) return 'wedding';
    if (['business','business_services'].includes(group)) return 'business';
    return group;
  };

  async function getCart() {
    const response = await nativeFetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('We could not verify your current cart.');
    return response.json();
  }

  function groupsInCart(cart) {
    const groups = new Set();
    let hasUnclassified = false;
    for (const item of cart?.items || []) {
      const value = canonicalGroup(item?.properties?.[GROUP_PROPERTY]);
      if (value) groups.add(value);
      else hasUnclassified = true;
    }
    return { groups: [...groups], hasUnclassified };
  }

  function messageFor(incoming, existing) {
    const incomingLabel = GROUP_LABELS[incoming] || incoming;
    const existingLabel = GROUP_LABELS[existing] || existing;
    if (incoming === 'calca_dropship' || existing === 'calca_dropship') {
      return 'DTF Supplies are fulfilled directly by CALCA and must be purchased in their own order.';
    }
    if (incoming === 'wedding' || existing === 'wedding') {
      return 'Wedding Stationery uses a dedicated production and fulfillment workflow and must be purchased in its own order.';
    }
    if (incoming === 'business' || existing === 'business') {
      return 'Business Services use a dedicated project and fulfillment workflow and must be purchased in their own order.';
    }
    if (incoming === 'personalized_items' || existing === 'personalized_items') {
      return 'Personalized Items, such as engraved or customized gifts, use a separate production workflow and must be purchased in their own order.';
    }
    return `${incomingLabel} cannot be combined with ${existingLabel} in the same checkout.`;
  }

  function ensureDialog() {
    let dialog = document.querySelector('[data-me-fulfillment-dialog]');
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'me-fulfillment-dialog';
    dialog.dataset.meFulfillmentDialog = '';
    dialog.innerHTML = `
      <form method="dialog" class="me-fulfillment-dialog__card">
        <button class="me-fulfillment-dialog__close" value="cancel" aria-label="Close">×</button>
        <p class="me-fulfillment-dialog__eyebrow">SEPARATE CHECKOUT REQUIRED</p>
        <h2>This item needs a separate order</h2>
        <p data-me-fulfillment-message></p>
        <div class="me-fulfillment-dialog__actions">
          <button class="button" value="cancel">Keep my current cart</button>
          <a class="button button-secondary" href="${root()}cart">View current cart</a>
        </div>
        <p class="me-fulfillment-dialog__note">This separation keeps production timing, shipping, and tracking clear for each order.</p>
      </form>`;
    document.body.appendChild(dialog);
    return dialog;
  }

  function showBlocked(message) {
    const dialog = ensureDialog();
    const target = dialog.querySelector('[data-me-fulfillment-message]');
    if (target) target.textContent = message;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else {
      dialog.setAttribute('open', '');
      dialog.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  async function guard(incomingGroup, options = {}) {
    const group = canonicalGroup(incomingGroup);
    if (!group) return true;
    try {
      const cart = await getCart();
      if (!cart?.item_count) return true;
      const { groups, hasUnclassified } = groupsInCart(cart);
      if (hasUnclassified && !groups.length) {
        showBlocked('Your cart contains an older item that cannot be automatically classified. Please finish or clear that order before starting a different fulfillment group.');
        return false;
      }
      const incompatible = groups.find((existing) => existing !== group);
      if (incompatible) {
        showBlocked(messageFor(group, incompatible));
        return false;
      }
      if (hasUnclassified && options.failClosed !== false) {
        showBlocked('One item already in your cart is missing fulfillment information. Please open the cart and remove or re-add that item before continuing.');
        return false;
      }
      return true;
    } catch (error) {
      showBlocked(error?.message || 'We could not verify whether this item can be combined with your current cart. Please try again.');
      return false;
    }
  }

  function formGroup(form) {
    return canonicalGroup(form?.querySelector(`input[name="properties[${GROUP_PROPERTY}]"]`)?.value || form?.dataset?.meFulfillmentGroup);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target?.closest?.('form[action*="/cart/add"], form[data-type="add-to-cart-form"], .shopify-product-form');
    if (!form || form.dataset.meFulfillmentGuardReplay === 'true') return;
    const group = formGroup(form);
    if (!group) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    guard(group).then((ok) => {
      if (!ok) return;
      form.dataset.meFulfillmentGuardReplay = 'true';
      if (typeof form.requestSubmit === 'function') form.requestSubmit(event.submitter || undefined);
      else form.submit();
      setTimeout(() => delete form.dataset.meFulfillmentGuardReplay, 1000);
    });
  }, true);

  function pageGroup() {
    return canonicalGroup(document.body?.dataset?.mePageFulfillmentGroup);
  }

  function inferAndStampRequest(input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = clean(init?.method || (typeof input !== 'string' ? input?.method : '') || 'GET').toUpperCase();
    if (method !== 'POST' || !String(url).includes('cart/add')) return { group: '', input, init };

    let group = '';
    let nextInit = { ...init };
    const body = init?.body;

    if (body instanceof FormData) {
      group = canonicalGroup(body.get(`properties[${GROUP_PROPERTY}]`)) || pageGroup();
      if (group && !body.get(`properties[${GROUP_PROPERTY}]`)) body.set(`properties[${GROUP_PROPERTY}]`, group);
      return { group, input, init: nextInit };
    }

    if (typeof body === 'string') {
      const contentType = clean(init?.headers?.['Content-Type'] || init?.headers?.['content-type']);
      if (contentType.includes('application/json')) {
        try {
          const payload = JSON.parse(body);
          if (Array.isArray(payload.items)) {
            group = canonicalGroup(payload.items.find((item) => clean(item?.properties?.[GROUP_PROPERTY]))?.properties?.[GROUP_PROPERTY]) || pageGroup();
            if (group) {
              payload.items = payload.items.map((item) => ({
                ...item,
                properties: { ...(item.properties || {}), [GROUP_PROPERTY]: canonicalGroup(item?.properties?.[GROUP_PROPERTY]) || group }
              }));
            }
          } else {
            group = canonicalGroup(payload?.properties?.[GROUP_PROPERTY]) || pageGroup();
            if (group) payload.properties = { ...(payload.properties || {}), [GROUP_PROPERTY]: group };
          }
          nextInit.body = JSON.stringify(payload);
        } catch (_) {}
      }
    }
    return { group, input, init: nextInit };
  }

  window.fetch = async function meFulfillmentFetch(input, init = {}) {
    const stamped = inferAndStampRequest(input, init);
    if (!stamped.group) return nativeFetch(stamped.input, stamped.init);
    const ok = await guard(stamped.group);
    if (!ok) {
      return new Response(JSON.stringify({
        status: 409,
        message: 'Separate checkout required',
        description: 'This item belongs to a different fulfillment workflow and must be purchased in a separate order.'
      }), { status: 409, headers: { 'Content-Type': 'application/json' } });
    }
    return nativeFetch(stamped.input, stamped.init);
  };

  window.MEFulfillment = { guard, getCart, groupsInCart, groupProperty: GROUP_PROPERTY, version: VERSION };
})();
